/**
 * sl-m82f.4.2: Generic "labeled items" feature tab.
 *
 * Intelligent Search, AI Memory and Business Intelligence all show the same
 * shape: a list of `{ sourceId, label, credits, callCount }` items. Instead
 * of three near-identical tab components, they each reduce to a thin
 * wrapper around this component — DRY.
 */
import { useI18n } from '@affine/i18n';
import { useCallback, useMemo } from 'react';

import { DocNameCell } from '../doc-name-cell';
import { FeatureTabShell } from '../feature-tab-shell';
import { formatCount, formatCredits, truncate } from '../format';
import type { SummaryCardData } from '../tab-summary-card';
import type { AnalyticsPeriodRange, BreakdownRow } from '../types';
import { useAsyncResource } from '../use-async-resource';

interface LabeledItem {
  sourceId: string | null;
  label: string;
  credits: number;
  callCount: number;
}

export interface LabeledFeatureTabProps {
  /** Cache key prefix, e.g. `"intelligent-search"`. */
  cacheKey: string;
  workspaceId: string;
  period: string | AnalyticsPeriodRange;
  /** Total credits for this tab — drives the first summary card. */
  featureCredits: number;
  /** Fetcher returning the list of labeled items. */
  fetcher: (
    workspaceId: string,
    period: string | AnalyticsPeriodRange
  ) => Promise<LabeledItem[]>;
  /** Column header / row name, e.g. "Document" / "Agent". */
  nameHeader: string;
  /** Units label for the second summary card, e.g. "Documents analysed". */
  itemsLabel: string;
  /** "Top X" label for the third summary card, e.g. "Top document". */
  topLabel: string;
  /** Empty-state text, e.g. "No documents analysed in this period". */
  emptyLabel?: string;
  /** Column header for call count, defaults to "Operations". */
  countLabel?: string;
  /**
   * When true the row `sourceId` is a workspace docId: the name column
   * resolves the live document title and renders an open-doc button instead
   * of the raw id. Leave false for non-doc sources (e.g. AI Memory bots).
   */
  docSource?: boolean;
}

export const LabeledFeatureTab = ({
  cacheKey,
  workspaceId,
  period,
  featureCredits,
  fetcher,
  nameHeader,
  itemsLabel,
  topLabel,
  emptyLabel,
  countLabel,
  docSource = false,
}: LabeledFeatureTabProps) => {
  const t = useI18n();
  const periodCacheKey =
    typeof period === 'string'
      ? period
      : `${period.key}:${period.since}:${period.until}`;

  const runFetch = useCallback(
    () => fetcher(workspaceId, period),
    [fetcher, workspaceId, period]
  );

  const { data, isLoading, error } = useAsyncResource(
    `${cacheKey}:${workspaceId}:${periodCacheKey}`,
    runFetch
  );

  const rows: BreakdownRow[] = useMemo(() => {
    if (!data) return [];
    return data.map((item, index) => ({
      id: item.sourceId ?? `${cacheKey}-${index}`,
      name: item.label,
      credits: item.credits,
      calls: item.callCount,
      docId: item.sourceId,
    }));
  }, [data, cacheKey]);

  // Doc-keyed tabs resolve the live document title frontend-side (the backend
  // label is a truncated id fallback) and expose an open-doc button. Rows
  // without a docId (the unattributed "Unknown" bucket) keep the plain label.
  const renderName = useCallback(
    (row: BreakdownRow) =>
      row.docId ? <DocNameCell docId={row.docId} /> : row.name,
    []
  );

  const topItem = useMemo(() => {
    if (rows.length === 0) return null;
    return rows.reduce(
      (best, row) => (row.credits > best.credits ? row : best),
      rows[0]
    );
  }, [rows]);

  const summary: readonly [SummaryCardData, SummaryCardData, SummaryCardData] =
    [
      {
        label:
          t[
            'com.streamlain.settings.workspace.ai.analytics.card.total-credits'
          ]?.() ?? 'Total credits',
        value: formatCredits(featureCredits),
      },
      {
        label: itemsLabel,
        value: formatCount(rows.length),
      },
      {
        label: topLabel,
        value: topItem ? truncate(topItem.name, 24) : '-',
        subtext: topItem
          ? `${formatCredits(topItem.credits)} credits`
          : undefined,
        smallValue: true,
      },
    ] as const;

  return (
    <FeatureTabShell
      summary={summary}
      rows={rows}
      nameHeader={nameHeader}
      countLabel={countLabel}
      emptyLabel={emptyLabel}
      isLoading={isLoading}
      error={error}
      hideUserFilter
      hideModelFilter
      resetSignal={periodCacheKey}
      renderName={docSource ? renderName : undefined}
    />
  );
};
